import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState
} from 'react'

import { FilterIDs, type FilterProps } from '@dcxwam/dcx-wam-ui-foundations'

import type { TagGroup, Tag } from '../../../../services/product/product-data'
import type { FilterStashType, HistoryFilter } from '../utils'
import {
  filterHistoryByStash,
  getEnabledFilters,
  getHistoryFromStash,
  scrollToElementWithOffset,
  SORT_PARAM_KEY,
  sortFilterOptions
} from '../utils'
import { pushDataLayerSelectedFilter } from '../../common/datalayer/product'
import {
  removeParams,
  setMultipleParams,
  setParams
} from '../../../../utils/navigation'
import type {
  GridListFilterConfig,
  GroupedItem,
  ProductListConfig
} from '../models'
import type { FilterState } from '../models/hooks'
import { filtersReducer } from '../reducers/filters-reducer'

interface UseFiltersParams {
  filters: ProductListConfig['filters']
  gridListFilterConfig: GridListFilterConfig
  data:
    { items: GroupedItem[]; facets: TagGroup[]; gcodes: string[] } | undefined
  currentParams: Record<string, string>
  paramsReady: boolean
  push: (url: URL) => void
  replace: (url: URL) => void
  isSsr: boolean
  containerRef: React.RefObject<HTMLElement | null>
}

const initialState: FilterState = {
  filtersStash: {},
  backupFiltersStash: null,
  historyFilters: [],
  backupHistoryFilters: [],
  sort: '',
  scrollToContainer: false
}

export const useFilters = ({
  filters,
  gridListFilterConfig,
  data,
  currentParams,
  paramsReady,
  push,
  replace,
  isSsr,
  containerRef
}: UseFiltersParams) => {
  const [state, dispatch] = useReducer(filtersReducer, initialState)
  const [disableInteractions, setDisableInteractions] = useState(true)

  const getEnabledHistoryFilterIds = useCallback(
    () =>
      filters?.filterItems
        ?.filter(
          (f: { applyWithinProductCards: boolean }) => f.applyWithinProductCards
        )
        ?.map((f: { tagID: string }) => f.tagID) ?? [],
    [filters?.filterItems]
  )

  const setHistory = useCallback(
    (filterStash: FilterStashType, field: HistoryFilter) => {
      const enabledHistoryFilterIds = getEnabledHistoryFilterIds()
      const newValues = filterHistoryByStash(
        filterStash,
        state.historyFilters,
        enabledHistoryFilterIds
      )
      if (
        newValues.length < state.historyFilters.length ||
        !enabledHistoryFilterIds.includes(field.filterId)
      ) {
        dispatch({ type: 'SET_HISTORY', payload: newValues })
      } else {
        dispatch({
          type: 'SET_HISTORY',
          payload: [...state.historyFilters, field]
        })
      }
    },
    [getEnabledHistoryFilterIds, state.historyFilters]
  )

  const updateQueryParams = useCallback(
    (key: string, values: string[], isPushed: boolean = true) => {
      if (!isSsr) {
        const value = values.join(',')
        const url = new URL(window?.location?.href)
        const newParams = new URLSearchParams(
          setParams(currentParams, key, value)
        )
        url.search = newParams.toString()
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        isPushed ? push(url) : replace(url)
      }
    },
    [currentParams, push, replace, isSsr]
  )

  const updateMultipleParams = useCallback(
    (newData: { [key: string]: string[] }, isPushed: boolean = true) => {
      if (!isSsr) {
        const paramsData = Object.entries(newData).reduce(
          (acc, [key, values]) => ({
            ...acc,
            [key]: values.join(',')
          }),
          {}
        )
        const url = new URL(window?.location?.href)
        const newParams = new URLSearchParams(
          setMultipleParams(currentParams, paramsData)
        )
        url.search = newParams.toString()
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        isPushed ? push(url) : replace(url)
      }
    },
    [currentParams, push, replace, isSsr]
  )

  const removeQueryParams = useCallback(
    (keys: string[]) => {
      if (!isSsr) {
        const url = new URL(window?.location?.href)
        const newParams = new URLSearchParams(removeParams(currentParams, keys))
        url.search = newParams.toString()
        push(url)
      }
    },
    [currentParams, push, isSsr]
  )

  const initFindFacet = useCallback(
    (facets: Array<TagGroup>, currTagGroupId: string) =>
      facets?.find((f) => f?.tagGroupId === currTagGroupId),
    []
  )

  const initFilterOptions = useCallback(
    (facet?: TagGroup, paramValues?: string[]) =>
      facet?.tags
        ?.filter((o) => paramValues?.includes(o?.tagId))
        ?.map((o) => o?.tagId) ?? [],
    []
  )

  const initFilterData = useCallback(
    () =>
      filters?.filterItems?.reduce(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (acc: any, { tagID: currTagGroupId }: { tagID: string }) => {
          const facet = initFindFacet(data?.facets || [], currTagGroupId)
          const paramValues = currentParams[currTagGroupId]?.split(',') ?? []
          const values = initFilterOptions(facet, paramValues)
          return {
            ...acc,
            ...(currTagGroupId && facet ? { [currTagGroupId]: values } : {})
          }
        },
        {}
      ) ?? {},
    [
      filters?.filterItems,
      initFindFacet,
      data?.facets,
      currentParams,
      initFilterOptions
    ]
  )

  const initSortData = useCallback(
    () =>
      gridListFilterConfig?.sortFilterOptions?.find(
        ({ filteroptionLabelValue }) =>
          currentParams[SORT_PARAM_KEY] === filteroptionLabelValue
      )?.filteroptionLabelValue ?? '',
    [gridListFilterConfig, currentParams]
  )

  const getInitFilterData = useRef(() => ({
    initFilters: {},
    initSort: ''
  }))

  const enabledFilters = useMemo(
    () => (data ? getEnabledFilters(data, filters, state.filtersStash) : {}),
    [data, filters, state.filtersStash]
  )

  const makeOnChangeFilter = useCallback(
    (
      tagGroupId: string,
      datalayerFilterCategory?: string,
      facetTagGroupId?: string
    ) =>
      (
        updatedFilter: Array<{
          id?: string
          text?: string
          checked?: boolean
        }>,
        index: number
      ) => {
        const newValues = updatedFilter
          .filter((f) => f.checked && f.id !== FilterIDs.All)
          .map((f) => f.id ?? '')
          .filter(Boolean)

        const newFilterStashState = {
          ...state.filtersStash,
          [tagGroupId]: newValues
        }

        dispatch({ type: 'SET_FILTER', payload: newFilterStashState })

        setHistory(newFilterStashState, {
          filterId: tagGroupId,
          value: updatedFilter[index]?.id ?? ''
        })

        updateQueryParams(tagGroupId, newValues, true)

        pushDataLayerSelectedFilter(
          datalayerFilterCategory ?? '',
          facetTagGroupId ?? '',
          updatedFilter[index]?.text ?? ''
        )
      },
    [setHistory, updateQueryParams, state.filtersStash]
  )

  const filtersData = useMemo(() => {
    const dataFilters = filters?.filtersVisibility
      ? {
          title: gridListFilterConfig?.filtersTitle,
          filterList: filters?.filterItems?.reduce(
            (
              acc: FilterProps[],
              {
                tagID: tagGroupId,
                title,
                description,
                allOptionVisibility,
                sortOptionsBy,
                sortByRegex,
                datalayerFilterCategory
              }
            ) => {
              const facet = data?.facets?.find(
                (f: { tagGroupId: string }) => f?.tagGroupId === tagGroupId
              )
              const checkedIds = state.filtersStash[tagGroupId] ?? []
              const enabledIds = enabledFilters[tagGroupId] ?? []
              const tags = facet?.tags
              const onChangeFilter = makeOnChangeFilter(
                tagGroupId,
                datalayerFilterCategory,
                facet?.tagGroupId
              )

              return facet
                ? [
                    ...acc,
                    {
                      title,
                      description,
                      id: facet.tagGroupId,
                      name: facet.tagGroupId,
                      message: '-',
                      all: allOptionVisibility,
                      allLabel: gridListFilterConfig?.filterLabel,
                      onChange: onChangeFilter as NonNullable<
                        FilterProps['onChange']
                      >,
                      checkboxList: tags
                        ?.sort(
                          sortFilterOptions({ sortOptionsBy, sortByRegex })
                        )
                        ?.reduce(
                          (
                            options: NonNullable<FilterProps['checkboxList']>,
                            { tagId, displayName: text, background: color }: Tag
                          ) => {
                            const values = {
                              id: tagId,
                              text,
                              checked: checkedIds.includes(tagId),
                              disabled: !enabledIds.includes(tagId),
                              ...(color ? { color } : {})
                            }

                            return [...options, values]
                          },
                          []
                        )
                    }
                  ]
                : acc
            },
            []
          )
        }
      : undefined
    return dataFilters
  }, [
    data?.facets,
    enabledFilters,
    filters?.filterItems,
    filters?.filtersVisibility,
    state.filtersStash,
    gridListFilterConfig?.filterLabel,
    gridListFilterConfig?.filtersTitle,
    makeOnChangeFilter
  ])

  const onSortChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, value?: string) => {
      const newValue = value ?? ''
      dispatch({ type: 'SET_SORT', payload: newValue })

      updateQueryParams(SORT_PARAM_KEY, [newValue], true)

      pushDataLayerSelectedFilter(
        gridListFilterConfig?.sortFilterTitle,
        e.target.name,
        newValue
      )
    },
    [gridListFilterConfig?.sortFilterTitle, updateQueryParams]
  )

  const sortData = useMemo(() => {
    const dataSort =
      filters?.filtersVisibility && filters?.sortFilterEnabled !== false
        ? {
            title: gridListFilterConfig?.sortFilterTitle,
            name: 'sorting-filter',
            id: 'sorting-filter',
            message: '-',
            onChange: onSortChange,
            value: state.sort,
            radioButtons: gridListFilterConfig?.sortFilterOptions?.map(
              ({
                filteroptionLabel: label,
                filteroptionLabelValue: value
              }) => ({
                label,
                value,
                checked: value === state.sort
              })
            )
          }
        : undefined
    return dataSort
  }, [
    filters?.filtersVisibility,
    filters?.sortFilterEnabled,
    gridListFilterConfig,
    onSortChange,
    state.sort
  ])

  const dialogButtonsCTA = useMemo(
    () => ({
      openProps: {
        label: gridListFilterConfig?.openFiltersLabel,
        onClick() {
          dispatch({ type: 'BACKUP' })
        }
      },
      closeProps: {
        'aria-label': gridListFilterConfig?.closeFiltersButtonArialabel,
        onClick() {
          dispatch({ type: 'RESTORE' })
          updateMultipleParams(state.backupFiltersStash as FilterStashType)
        }
      },
      applyProps: {
        label: gridListFilterConfig?.applyFiltersLabel,
        onClick() {
          dispatch({ type: 'RESTORE' })
        }
      },
      resetProps: {
        label: gridListFilterConfig?.removeFiltersLabel,
        onClick() {
          removeQueryParams([
            ...Object.keys(state.filtersStash),
            SORT_PARAM_KEY
          ])
          dispatch({ type: 'RESET' })
        }
      }
    }),
    [
      state.backupFiltersStash,
      state.filtersStash,
      gridListFilterConfig?.applyFiltersLabel,
      gridListFilterConfig?.closeFiltersButtonArialabel,
      gridListFilterConfig?.openFiltersLabel,
      gridListFilterConfig?.removeFiltersLabel,
      removeQueryParams,
      updateMultipleParams
    ]
  )

  // Effect: update getInitFilterData ref
  useEffect(() => {
    getInitFilterData.current = () => ({
      initFilters: initFilterData(),
      initSort: initSortData()
    })
  }, [
    data?.facets,
    filters?.filterItems,
    initFilterData,
    initSortData,
    currentParams
  ])

  // Effect: disable interactions until params are ready
  useEffect(() => {
    if (paramsReady) {
      setDisableInteractions(false)
    }
  }, [paramsReady])

  // Effect: init filters from URL params
  useEffect(() => {
    if (filters?.filterItems && data?.facets && paramsReady) {
      const { initFilters, initSort } = getInitFilterData.current()
      const enabledHistoryFilterIds = getEnabledHistoryFilterIds()
      const historyFilters = getHistoryFromStash(
        initFilters,
        enabledHistoryFilterIds
      )
      dispatch({
        type: 'INIT',
        payload: { filtersStash: initFilters, sort: initSort, historyFilters }
      })
    }
  }, [getEnabledHistoryFilterIds, data?.facets, paramsReady])

  // Effect: sync filtersStash when enabledFilters changes
  useEffect(() => {
    if (
      Object.entries(state.filtersStash).some(([key, value]) =>
        value.some((item) => !enabledFilters[key]?.includes(item))
      )
    ) {
      dispatch({ type: 'SYNC_ENABLED', payload: enabledFilters })

      const newFiltersStash = Object.entries(state.filtersStash).reduce(
        (acc, [key, value]) => ({
          ...acc,
          [key]: value.filter((item) => enabledFilters[key]?.includes(item))
        }),
        {} as FilterStashType
      )
      const enabledHistoryFilterIds = getEnabledHistoryFilterIds()
      updateMultipleParams(newFiltersStash, false)
      dispatch({
        type: 'SET_HISTORY',
        payload: filterHistoryByStash(
          newFiltersStash,
          state.historyFilters,
          enabledHistoryFilterIds
        )
      })
    }
  }, [
    getEnabledHistoryFilterIds,
    enabledFilters,
    state.filtersStash,
    state.historyFilters,
    updateMultipleParams
  ])

  // Effect: scroll to container
  useEffect(() => {
    if (!isSsr && state.scrollToContainer) {
      scrollToElementWithOffset(
        containerRef?.current,
        document.querySelector('header')?.offsetHeight || 0
      )

      dispatch({ type: 'SET_SCROLL', payload: false })
    }
  }, [state.scrollToContainer, isSsr])

  return {
    filtersStash: state.filtersStash,
    historyFilters: state.historyFilters,
    sort: state.sort,
    enabledFilters,
    filtersData,
    sortData,
    dialogButtonsCTA,
    disableInteractions
  }
}
